export const tsmtKommunikacioPlaybook = {
  seo: {
    title: "TSMT® online presence playbook — for TVTH members | Bespire",
    description:
      "Communications playbook for TSMT® therapists and LongiKid® assessors: Facebook, Google Business Profile, legal framework — and how Bespire makes it easy.",
  },
  hero: {
    eyebrow: "TVTH • Communications playbook",
    title: "TSMT® online presence — the starter kit",
    subtitle:
      "A practical guide for TVTH members to run Facebook and Google Business Profile in a credible, legally compliant way that leverages the brand. Bespire stands by you so you can actually do it with 2–4 hours a week.",
    stats: [
      { value: "16", label: "Facebook steps" },
      { value: "5", label: "Google steps" },
      { value: "2–4h", label: "weekly on marketing" },
    ],
    ctaPrimary: "Free trial on Bespire",
    ctaSecondary: "TSMT® partner page",
    disclaimer:
      "Based on the official material of the TSMT® Model team. As a TVTH member you are entitled to use the TSMT® and LongiKid® brand names and logos.",
  },
  goldenRules: {
    eyebrow: "Golden rules",
    title: "The most important frames before you post anything",
    subtitle:
      "These rules run through the entire playbook. If you just remember these, you already own 80% of credibility.",
    items: [
      {
        title: "That famous ®",
        description:
          "TSMT® and LongiKid® are trademark-protected. As a TVTH member — with your membership fee — you may use the name, the logo and the brand value. Nationally known brand with wide central reach, which you can leverage for your own practice.",
        highlight: "TSMT® and LongiKid® — always with ®.",
      },
      {
        title: "Official logos",
        description:
          "On images and infographics use only the official TSMT® and LongiKid® logo. The new LongiKid® logo will be rolled out in the summer of 2026 — by late autumn 2026 everything will be swapped, and you will be asked to update too. You may freely use your own logo alongside the official ones.",
        highlight: "Own logo freely, TSMT®/LongiKid® only the official version.",
      },
    ],
    logoConsequencesTitle: "Why does this matter? If you don't use the official logo…",
    logoConsequences: [
      "Parents will think this is a «shady» business — you lose trust.",
      "You miss out on the established, recognized logo building credibility for you.",
      "The perception of professionalism drops — which is a baseline expectation today.",
      "If we notice it, we'll ask you to fix it — an extra loop that can be avoided.",
    ],
    bespireTie: {
      title: "How can Bespire help?",
      description:
        "Bespire helps you build a consistent, professional online presence so your credibility and brand value are preserved across every surface.",
      linkLabel: "Download official logo",
    },
  },
  childrenInVideo: {
    eyebrow: "Sensitive topic",
    title: "Children in videos",
    subtitle:
      "A very sensitive topic — but with clear frames you can post confidently. The worst that can happen: you're asked to modify. So go for it!",
    legalNotice: {
      label: "Recommended closing sentence for the description (if a child appears in the recording):",
      text:
        "The exercises shown in the video serve exclusively illustrative and educational purposes and do not replace individual examination or professional advice. The legal representatives of the children shown in the recording have given prior written consent to the recording of their image and voice and to publishing the video for communication purposes. Copying and further use of the recording without permission is prohibited.",
    },
    doList: {
      title: "Do",
      items: [
        "Always get written parental consent for recording and publishing the child's image.",
        "If there's no consent, the child must not be recognisable (from behind, face covered, drawing or illustration).",
        "Show realistic exercises that can be done at home too — the parent should believe they can do it too.",
        "Show only parts of a LongiKid® assessment, focused on the «why» — building the parent's sense of safety.",
      ],
    },
    dontList: {
      title: "Don't",
      items: [
        "Record a full LongiKid® assessment from start to finish.",
        "Photograph LongiKid® base sheets — only snapshots without the data sheet.",
        "Post the TSMT® drawn task series — showing a few tasks is fine.",
        "Post a parent or child without their consent.",
        "Head-down exercises or exercises with extreme vestibular stimuli.",
        "LongiKid® assessment opinions with personal data or identifiable symptoms.",
      ],
    },
    additional: {
      title: "Further suggestions",
      items: [
        {
          title: "TSMT® equipment tasks",
          body:
            "You can freely show a balance-board or big-ball exercise — respecting the restrictions above, always with proper explanation.",
        },
        {
          title: "LongiKid® opinions",
          body:
            "Only without personal data and with wording from which the subject cannot be inferred. Don't state the exact date and avoid narrow symptom clusters — in a small community the child can still be recognised.",
        },
      ],
    },
    escalationTitle: "What to do with a critical comment?",
    escalation:
      "If you get a critical comment and it does NOT come from a TVTH therapist or TSMT® trainer, handle it yourself. If per the above guidelines the video turned out debatable, you can flag it to the central team at szakmai.vezeto@tsmtmodell.hu.",
    bespireTie: {
      title: "How can Bespire help?",
      description:
        "Bespire helps you keep child protection and legal guidelines in mind, so your content can be created safely and appropriately.",
    },
  },
  facebook: {
    eyebrow: "Facebook",
    title: "Professional practice page — in 16 steps",
    subtitle:
      "The guide is for TSMT® therapists who want a credible, trust-building, legally compliant presence. The goal isn't to be perfect on day one — it's to start.",
    pageNames: {
      title: "Page name",
      subtitle:
        "Facebook → Menu → Pages → Create new page. Name = your name + TSMT + location. Everything else goes into the description. (Facebook does not allow the ® symbol in the page name field.)",
      good: [
        "Anna Kovács TSMT therapist",
        "Anna Kovács – Certified TSMT therapist",
        "TSMT Development – Győr – Anna Kovács",
      ],
      bad: ["Best TSMT", "ADHD Healer", "Guaranteed development"],
      goodLabel: "Recommended",
      badLabel: "Avoid",
    },
    coverExample: {
      title: "A cover photo that tells everything in 3 seconds",
      subtitle:
        "What you do • who you help • how to book. A good example:",
      lines: [
        "TSMT® therapist",
        "Infant and child development",
        "Assessment • Therapy • Parent consultation",
        "📍 Budapest XVII.",
      ],
    },
    steps: [
      {
        n: "01",
        title: "Why not use your personal profile?",
        body: "Personal profile and professional activity are two different things. On a professional page:",
        list: [
          "Professional appearance",
          "Searchable on Google",
          "Ads can be run",
          "Statistics are available",
          "Multiple admins can manage it",
          "No need to friend your followers",
          "Complies with Meta's recommendations",
        ],
      },
      {
        n: "02",
        title: "Create the page",
        body:
          "Facebook → Menu → Pages → Create new page. Name = your name + TSMT + location. Concrete suggestions and names to avoid are in the «Page name» block above.",
      },
      {
        n: "03",
        title: "Profile photo",
        body: "Always good quality. Preferably smiling, natural, light background, professional attire.",
        list: ["Don't use a selfie", "Don't use a holiday photo", "Don't use a family photo", "Don't use a photo with children"],
      },
      {
        n: "04",
        title: "Cover photo",
        body:
          "In 3 seconds it tells: what you do, who you help, how to book. Concrete example in the «Cover photo» block above.",
      },
      {
        n: "05",
        title: "About / Intro",
        body:
          "Keep it short. Be creative — this is where you show your own voice; you can even use common questions. Make it personal, not generic.",
        badExampleLabel: "Avoid (AI-generated, generic):",
        badExample:
          "I'm a TSMT® therapist. I support the motor, attention and neurological development of young children. My goal is that families get help and professional support on time.",
        goodExampleLabel: "Much better (personal, with a question):",
        goodExample:
          "«Why isn't he talking yet? The other kids are already running, why isn't he?» I help parents with questions like these.",
      },
      {
        n: "06",
        title: "Contact details",
        body: "Always fill them in, otherwise people can't find you and the previous 5 points go to waste :)",
        list: ["Phone number", "Email", "Website", "Address", "Messenger"],
        footer: "The more contact options, the higher the trust.",
      },
      {
        n: "07",
        title: "Set up the button",
        body: "Recommended buttons to choose from:",
        list: [
          "Contact",
          "Book appointment",
          "Send message — if you pick this, set office hours so you protect your privacy and don't burn out.",
        ],
      },
      {
        n: "08",
        title: "What to post?",
        body:
          "Rule of thumb — adjust freely: 40% education, 30% trust-building, 20% community, 10% service. Detailed breakdown below.",
      },
      {
        n: "09",
        title: "Images",
        body: "Images give the first impression of a post — watch the quality.",
        useLabel: "Use",
        use: [
          "Your own photos — no studio needed, in fact.",
          "Infographics (we'll show how later).",
          "Illustrations.",
          "As a TVTH member, the official TSMT® and LongiKid® logo.",
        ],
        avoidLabel: "Avoid",
        avoid: [
          "Too much stock photography — very impersonal.",
          "Pixelated, poor quality images — this is baseline, not premium anymore.",
        ],
      },
      {
        n: "10",
        title: "Videos (the scary one :D)",
        body:
          "Facebook currently favours videos — you can only leverage the above if you work on this.",
        recommendedLabel: "Recommended",
        recommended: [
          "30–90 seconds long",
          "A hook message at the start that grabs attention",
          "Be spontaneous and human — it's OK to fumble a bit 🙂",
        ],
        topicsLabel: "Topic ideas",
        topics: [
          "Developmental play",
          "Balance",
          "Attention",
          "School readiness",
          "Sensory processing",
          "LongiKid® assessment goals and results",
          "Cases, stories",
        ],
      },
      {
        n: "11",
        title: "Handling comments",
        body:
          "Always reply if you can — that's the point of the page. Ideally within 24 hours. What you must never do: diagnose or give opinions in a comment.",
        templateLabel: "Use this template response instead:",
        template:
          "«Thanks for the question! Every child is different, so responsible opinion isn't possible based on this much information. If you'd like, please reach out and we'll discuss the details.»",
      },
      {
        n: "12",
        title: "What to avoid",
        body: "Never promise:",
        avoid: [
          "A guaranteed outcome",
          "Guaranteed progress",
          "Cure",
          "«ADHD will just go away»",
        ],
        footer: "…and similar things (obvious, but better to write it down :))",
      },
      {
        n: "13",
        title: "Photos of children",
        body: "Always have written parental consent. Without it, the child must not be recognisable.",
        alternativesLabel: "Practical alternatives without consent",
        alternatives: ["From behind", "Face covered", "Drawing or illustration"],
      },
      {
        n: "14",
        title: "Hashtags",
        body:
          "We don't know exactly how much they still work with the new algorithm — but they're like toilet paper… if you don't have any it can be a problem 🙂 Use these types:",
        hashtags: [
          "#TSMT",
          "#childdevelopment",
          "#schoolreadiness",
          "#sensoryprocessing",
          "#earlydevelopment",
        ],
      },
      {
        n: "15",
        title: "Posting frequency",
        body:
          "The point is a continuous content habit — 2–4 hours of marketing weekly. The minimum to make progress:",
        list: [
          "2–3 posts a week",
          "1 video a week",
          "A daily Story (if you can) — it goes to Instagram as well",
        ],
      },
      {
        n: "16",
        title: "Reused content",
        body: "You may reuse any content from the TSMT® Model page and add your own thoughts.",
        sourceLabel: "If you save an image and post it as your own, always credit the source:",
        sourceUrl: "facebook.com/tsmtmodell",
      },
    ],
    postMix: {
      title: "Post mix — the rule of thumb",
      items: [
        { pct: 40, label: "Education", desc: "What is TSMT®? What is it good for? When should you get assessed?" },
        { pct: 30, label: "Trust-building", desc: "Gym, your own work, parent testimonials (very effective)." },
        { pct: 20, label: "Community", desc: "Events, workshops, talks." },
        { pct: 10, label: "Service", desc: "Assessment, applying, appointments." },
      ],
    },
  },
  bespireXsteps: {
    eyebrow: "Bespire × 16 steps",
    title: "What Bespire takes off your plate",
    subtitle:
      "Features built on the playbook rules — so you don't just read it, you actually do it.",
    items: [
      { step: "06", stepTitle: "Contact details made easy", body: "Bespire helps keep your contact details consistent across every surface so parents can always reach you." },
      { step: "07", stepTitle: "Book appointment button", body: "Wire your Bespire booking link to the Facebook «Book» button — parents get a slot in 3 clicks." },
      { step: "08", stepTitle: "Post mix 40/30/20/10", body: "The AI assistant plans a schedule in this ratio for Facebook, Instagram and TikTok." },
      { step: "11", stepTitle: "Comments within 24h", body: "The message center notifies you so you don't miss the 24-hour window — template replies ready. (Coming soon)" },
      { step: "09", stepTitle: "Images and infographics", body: "Create professional post images and infographics in minutes — no need to hire a designer." },
      { step: "14", stepTitle: "Hashtag library", body: "TSMT® and LongiKid® specific hashtag bundles you can attach to a post with one click." },
      { step: "15", stepTitle: "Story cross-posting", body: "One Story lands on Facebook and Instagram — no need to upload twice." },
      { step: "10", stepTitle: "Video ideas and scripts", body: "The AI assistant suggests topics and short scripts for your 30–90 second videos, hook message included." },
      { step: "08", stepTitle: "Content calendar", body: "See the next weeks' posts in one clear calendar — never run out of ideas, never post randomly." },
      { step: "16", stepTitle: "Website and booking in one", body: "Parents coming from Facebook and Google land on a credible website where they can book right away — no separate system to run." },
    ],
  },
  google: {
    eyebrow: "Google Maps",
    title: "Google Business Profile — 5 steps",
    subtitle:
      "When parents and institutional experts search, Google is still first. If you're not there, you're invisible.",
    steps: [
      {
        title: "Create a Google Business Profile",
        description:
          "Google Business Profile lets your practice show up in Google Search and Maps.",
        substeps: [
          "Open business.google.com.",
          "Sign in with your Google account (Gmail). If you don't have one, create one first.",
          "Click «Add business».",
          "Enter the name — use your own name and professional title.",
          "Pick the best matching category for your activity.",
        ],
        nameExamplesLabel: "Name suggestions",
        nameExamples: ["Anna Kiss – TSMT® therapist", "Anna Kiss – LongiKid® assessor"],
        categoryLabel: "Category examples",
        categories: ["Development center", "Healthcare provider", "Occupational therapist"],
      },
      {
        title: "Fill in the key details",
        description:
          "Exact address, phone, website (if any), opening hours. If you have a Bespire website, enter it — it gets SEO power and booking function at once.",
      },
      {
        title: "Upload photos",
        description:
          "Quality photos of your practice, gym, logo and portrait — the same ones you posted to Facebook and your Bespire site.",
      },
      {
        title: "Write the intro",
        description:
          "Briefly present what you do and naturally mention your TSMT® and/or LongiKid® services. Keywords: child development, school readiness, sensory integration.",
      },
      {
        title: "Verify your profile",
        description:
          "Google verifies your business via video, email or another method. The profile shows on Maps only after verification.",
      },
    ],
    bespireTie: {
      title: "Consistent online presence",
      description:
        "Bespire helps keep your website, contact details and booking options consistent across platforms — so parents and partners always find accurate information.",
    },
  },
  checklist: {
    eyebrow: "Checklist",
    title: "Before you go live — tick it through",
    subtitle: "6 playbook items + 5 Bespire extras so your online presence not only launches, but keeps working day after day.",
    playbookLabel: "Playbook basics",
    playbook: [
      "Google Business Profile created",
      "Profile verified",
      "Phone number and address filled in",
      "Intro written",
      "Photos uploaded",
      "Data consistent across all online surfaces",
    ],
    bespireLabel: "Bespire extra",
    bespire: [
      "Your own website live — services, prices, gallery and contact in one place",
      "Online booking wired in — into the Facebook «Book» button and your Google Business Profile too",
      "First AI-generated posts scheduled to Facebook and Instagram",
      "Automatic confirmation and reminder emails set up for bookings",
      "Clients and leads in one place — with history, notes and status",
    ],
  },
  cta: {
    eyebrow: "Closing",
    title: "Let's do this together — post confidently",
    subtitle:
      "We hope the above helps establish your online presence and gives you the right frames and ammunition for a daily routine. Bespire will be there with you — website, booking, AI assistant in one place.",
    primary: "Free trial — 3 days, 30 credits as a gift",
    secondary: "TSMT® partner page",
    tertiary: "Webinar signup",
    signoff: "Go for it and good luck! — The TSMT® Model team",
    source:
      "Source: TSMT® Model team — TVTH communications playbook. © 2023-2026 TSMT-HRG College Kft. All rights reserved.",
  },
};
